import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, FileCode2, Plus, RotateCcw } from 'lucide-react';
import type { DepartmentRow, DepartmentWithAgents } from '@command-room/shared';
import { DepartmentDialog } from '@/components/department-dialog/DepartmentDialog';
import { DepartmentSection } from '@/components/department-section/DepartmentSection';
import { OrgChart } from '@/components/org-chart/OrgChart';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  useAgents,
  useDepartments,
  useExportOrganization,
  useInfraSettings,
  useUpdateInfraSettings,
} from '@/api/queries';

const ORG_KEY = 'organization_text';

const TABS = ['company', 'structure', 'departments'] as const;
type OrgTab = (typeof TABS)[number];

/** The `?tab=` value, falling back to the structure chart for anything unknown. */
function parseTab(value: string | null): OrgTab {
  return TABS.find((t) => t === value) ?? 'structure';
}

/** Editor for the shared company description injected into every job description. */
function CompanyCard() {
  const { data: settings, isLoading } = useInfraSettings();
  const update = useUpdateInfraSettings();
  const exportFile = useExportOrganization();

  const stored = settings?.find((s) => s.key === ORG_KEY)?.value ?? '';
  const [draft, setDraft] = useState(stored);
  const [saved, setSaved] = useState(false);

  // Adopt the server value once it arrives (and after any external change).
  useEffect(() => setDraft(stored), [stored]);

  const isDirty = draft !== stored;

  async function onSave() {
    await update.mutateAsync({ [ORG_KEY]: draft });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company</CardTitle>
        <p className="text-sm text-muted-foreground">
          Injected into the{' '}
          <code className="rounded bg-muted px-1 text-xs">&lt;Organization&gt;</code> block of every
          agent's job description. Export writes it back to{' '}
          <code className="rounded bg-muted px-1 text-xs">_parts/organization.xml</code> so the
          Python build tooling stays in sync.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {isLoading ? (
          <Skeleton className="h-40 rounded-md" />
        ) : (
          <Textarea
            rows={10}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="resize-y text-sm leading-relaxed"
            placeholder="Describe the organization in plain language…"
          />
        )}

        {(update.error || exportFile.error) && (
          <p className="flex items-start gap-1 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {((update.error ?? exportFile.error) as Error).message}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => exportFile.mutate()}
            disabled={exportFile.isPending || isDirty || !stored}
            title={isDirty ? 'Save your changes first' : undefined}
          >
            <FileCode2 className="h-3.5 w-3.5" />
            {exportFile.isPending ? 'Exporting…' : 'Export to organization.xml'}
          </Button>
          {exportFile.isSuccess && !isDirty && (
            <span className="flex items-center gap-1 text-sm text-success">
              <CheckCircle2 className="h-4 w-4" /> Exported
            </span>
          )}

          <div className="ml-auto flex items-center gap-3">
            {isDirty && (
              <Button type="button" variant="outline" size="sm" onClick={() => setDraft(stored)}>
                <RotateCcw className="h-3.5 w-3.5" />
                Discard
              </Button>
            )}
            <Button onClick={onSave} disabled={!isDirty || update.isPending}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
            {saved && !isDirty && (
              <span className="flex items-center gap-1 text-sm text-success">
                <CheckCircle2 className="h-4 w-4" /> Saved
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function OrganizationPage() {
  const { data: agents } = useAgents();
  const { data: departments, isLoading: deptsLoading } = useDepartments();
  const [deptDialogOpen, setDeptDialogOpen] = useState(false);
  const [editingDept, setEditingDept] = useState<DepartmentRow | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'));

  function onTabChange(next: string) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set('tab', next);
        return params;
      },
      { replace: true },
    );
  }

  function openCreate() {
    setEditingDept(undefined);
    setDeptDialogOpen(true);
  }

  function openEdit(dept: DepartmentRow) {
    setEditingDept(dept);
    setDeptDialogOpen(true);
  }

  const orderedDepts = useMemo((): DepartmentWithAgents[] => {
    if (!departments) return [];
    const roots = departments.filter((d) => d.parent_dept_id === null);
    const children = departments.filter((d) => d.parent_dept_id !== null);
    return [...roots, ...children];
  }, [departments]);

  const unassigned = useMemo(() => {
    if (!agents || !departments) return [];
    const knownNames = new Set(departments.map((d) => d.name));
    return agents.filter((a) => !a.department || !knownNames.has(a.department));
  }, [agents, departments]);

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Organization</h1>
          <p className="text-sm text-muted-foreground">
            Company description, department structure, and who reports where.
          </p>
        </div>
        <Button size="sm" onClick={openCreate}>
          <Plus className="mr-1.5 h-4 w-4" />
          New department
        </Button>
      </header>

      <Tabs value={tab} onValueChange={onTabChange}>
        <TabsList className="w-fit">
          <TabsTrigger value="company">Company</TabsTrigger>
          <TabsTrigger value="structure">Structure</TabsTrigger>
          <TabsTrigger value="departments">Departments</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-6">
          <CompanyCard />
        </TabsContent>

        <TabsContent value="structure" className="mt-6">
          {deptsLoading ? (
            <Skeleton className="h-64 rounded-lg" />
          ) : (
            <OrgChart
              departments={departments ?? []}
              agents={agents ?? []}
              unassigned={unassigned}
              onEdit={openEdit}
            />
          )}
        </TabsContent>

        <TabsContent value="departments" className="mt-6">
          {deptsLoading ? (
            <div className="flex flex-col gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-40 rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {orderedDepts.map((dept) => (
                <DepartmentSection key={dept.id} dept={dept} onEdit={openEdit} />
              ))}
              {unassigned.length > 0 && (
                <DepartmentSection
                  dept={{
                    id: -1,
                    name: 'Unassigned',
                    goal: null,
                    responsibilities: null,
                    head_agent_name: null,
                    parent_dept_id: null,
                    agents: unassigned,
                  }}
                />
              )}
              {orderedDepts.length === 0 && unassigned.length === 0 && (
                <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
                  No departments configured yet.
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <DepartmentDialog
        key={editingDept?.id ?? 'new'}
        initial={editingDept}
        open={deptDialogOpen}
        onOpenChange={setDeptDialogOpen}
      />
    </div>
  );
}
